import { useId, useState } from "react";

import { useDirectorContext } from "@contexts/director";
import QualityBanner from "../QualityBanner";
import DirectorUpdateButton from "../module1/DirectorUpdateButton";
import { DirectorBreadcrumb, DirectorRouteBreadcrumb } from "./DirectorBreadcrumb";
import DirectorFreshnessHeader from "./DirectorFreshnessHeader";
import DirectorScopeControls from "./DirectorScopeControls";
import DirectorSummaryCards from "./DirectorSummaryCards";

const EMPTY = [];

/**
 * Padrão das telas da Diretoria (spec `director/workspace-navigation`), nesta ordem fixa:
 * 1. cabeçalho: pergunta, estado dos dados e a ação de atualizar;
 * 2. resposta: avisos que mudam a leitura, a frase-resposta e até quatro cartões;
 * 3. ação principal da tela (quando houver);
 * 4. gráfico principal com os filtros da tela;
 * 5. detalhe tabular ou hierárquico;
 * 6. "Sobre estes números", recolhido, com premissas, cobertura, regras e avisos técnicos.
 *
 * `status` (carregando, erro, vazio) fica entre o cabeçalho e a resposta. `breadcrumb` é a trilha
 * explícita (ex.: a obra aberta numa tela principal); sem ela, vale a do mapa rota → seção.
 */
const DirectorPage = ({
  title,
  question,
  eyebrow,
  answer = null,
  warnings = EMPTY,
  cards = EMPTY,
  cardsLabel = "Indicadores principais",
  action = null,
  chart = null,
  detail = null,
  notes = null,
  status = null,
  breadcrumb = null,
  qualitySources = EMPTY,
  showProjectFilter = true,
  headerAction,
  onPublished,
  className = "",
}) => {
  const { selectedCompanyId, accessByCompany = {} } = useDirectorContext();
  const access = accessByCompany[selectedCompanyId] || {};
  const notesId = `${useId().replace(/:/g, "-")}-notes`;
  const [notesOpen, setNotesOpen] = useState(false);
  const visibleWarnings = warnings.filter(Boolean);
  const hasAnswer = Boolean(answer) || cards.some(Boolean) || visibleWarnings.length > 0 || qualitySources.length > 0;

  return <main className={`director-page director-module1 director-workspace ${className}`.trim()}>
    {breadcrumb
      ? <DirectorBreadcrumb items={breadcrumb.items} back={breadcrumb.back} />
      : <DirectorRouteBreadcrumb />}
    <header className="director-page__header director-module1__header director-workspace__header" data-block="header">
      <div className="director-workspace__heading">
        <span className="director-eyebrow">{eyebrow || `Diretoria · ${access.companyName || "portfólio"}`}</span>
        <h1>{title}</h1>
        {question && <p className="director-workspace__question">{question}</p>}
        <DirectorFreshnessHeader onPublished={onPublished} />
      </div>
      <div className="director-module1__header-actions">
        {headerAction === undefined ? <DirectorUpdateButton variant="compact" onSucceeded={onPublished} /> : headerAction}
        <DirectorScopeControls showProjectFilter={showProjectFilter} />
      </div>
    </header>
    {status}
    {hasAnswer && <div className="director-workspace__answer" data-block="answer">
      {visibleWarnings.length > 0 && <div className="director-workspace__warnings" role="note">
        {visibleWarnings.map((warning, index) => <div className="director-workspace__warning" key={index}>{warning}</div>)}
      </div>}
      <QualityBanner sources={qualitySources} />
      {answer && <p className="director-workspace__sentence" aria-live="polite">{answer}</p>}
      <DirectorSummaryCards cards={cards} label={cardsLabel} />
    </div>}
    {action && <div className="director-workspace__action" data-block="action">{action}</div>}
    {chart && <div className="director-workspace__chart" data-block="chart">{chart}</div>}
    {detail && <div className="director-workspace__detail" data-block="detail">{detail}</div>}
    {notes && <section className="director-notes" data-block="notes" aria-labelledby={`${notesId}-title`}>
      <h2 className="director-notes__title" id={`${notesId}-title`}>
        <button
          type="button"
          className="director-notes__toggle"
          aria-expanded={notesOpen ? "true" : "false"}
          aria-controls={notesId}
          onClick={() => setNotesOpen((open) => !open)}
        >
          Sobre estes números
        </button>
      </h2>
      <div className="director-notes__body" id={notesId} hidden={!notesOpen}>{notes}</div>
    </section>}
  </main>;
};

export default DirectorPage;
